import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["animate", "appearAnimation", "callOnOrderChangeOnDragEnd", "delay", "description", "direction", "disableAllAnimations", "disableDropOnHover", "duration", "easing", "enterAnimation", "getPosition", "items", "leaveAnimation", "maintainContainerHeight", "onOrderChange", "onFinish", "onFinishAll", "onStart", "onStartAll", "staggerDelayBy", "staggerDurationBy"],
  _excluded2 = ["id"];
import I18n from 'I18n';
import { useCallback, useEffect, useState } from 'react';
import FlipMove from 'react-flip-move';
import { callIfPossible } from 'foundations-components/transitional/utils/functions';
import update from '../../utils/update';
// @ts-ignore type UIDragListItem
import UIDragListItem from '../connected/UIDragListItem';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import styled, { useTheme } from 'foundations-theming/utils';
import { filterProps } from 'foundations-components/utils/filterProps';
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { deepMerge } from 'foundations-components/transitional/utils/deepMerge';

/**
 * react-flip-move types
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EMPTY_ITEMS = [];
const StyledOl = styled.ol.withConfig({
  displayName: "UISortableList__StyledOl"
})(({
  theme,
  $isHorizontal,
  $cssStyles
}) => {
  return Object.assign({
    // list-unstyled className styles
    listStyle: 'none',
    padding: theme.space['0'],
    margin: theme.space['0'],
    // flexDirection horizontal styles
    display: $isHorizontal ? 'flex' : undefined,
    justifyContent: $isHorizontal ? 'space-around' : undefined
  }, $cssStyles);
});
const UISortableList = _ref => {
  let {
      animate = true,
      appearAnimation,
      callOnOrderChangeOnDragEnd = false,
      delay = 0,
      description = 'Sortable list',
      direction = 'vertical',
      disableAllAnimations = false,
      disableDropOnHover = false,
      duration = 100,
      easing = 'cubic-bezier(.42, .0, .58, 1)',
      enterAnimation = 'elevator',
      getPosition = el => callIfPossible(el.getBoundingClientRect()),
      items: initialItems = EMPTY_ITEMS,
      leaveAnimation = 'elevator',
      maintainContainerHeight = false,
      onOrderChange,
      onFinish,
      onFinishAll,
      onStart,
      onStartAll,
      staggerDelayBy = 0,
      staggerDurationBy = 0
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const [items, setItems] = useState(() => initialItems);

  // Update our internal representation of `items` when the consumer
  // updates their list.
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);
  const uniqueId = useUniqueId('UISortableList-');
  const instructionsId = `${uniqueId}-instructions`;
  const listRole = 'grid';
  const moveItem = useCallback((dragIndex, hoverIndex) => {
    const newItems = update(items, {
      $splice: [[dragIndex, 1], [hoverIndex, 0, items[dragIndex]]]
    });
    setItems(newItems);
    if (onOrderChange) {
      onOrderChange(newItems);
    }
  }, [items, onOrderChange]);
  const onHover = useCallback((dragIndex, hoverIndex) => {
    const newItems = update(items, {
      $splice: [[dragIndex, 1], [hoverIndex, 0, items[dragIndex]]]
    });
    setItems(newItems);
  }, [items]);
  const renderItem = useCallback((itemProps, index) => {
    const {
        id
      } = itemProps,
      passthroughProps = _objectWithoutPropertiesLoose(itemProps, _excluded2);
    return /*#__PURE__*/_jsx("li", {
      "aria-describedby": instructionsId,
      role: "gridcell",
      children: /*#__PURE__*/_jsx(UIDragListItem, Object.assign({}, passthroughProps, {
        callOnOrderChangeOnDragEnd: callOnOrderChangeOnDragEnd,
        direction: direction,
        disableDropOnHover: disableDropOnHover,
        index: index,
        id: id,
        moveItem: moveItem,
        onHover: onHover
      }))
    }, id);
  }, [direction, instructionsId, moveItem, disableDropOnHover, onHover, callOnOrderChangeOnDragEnd]);
  const defaultStyles = {
    listStyle: 'none',
    padding: theme.space['0'],
    margin: theme.space['0'],
    display: direction === 'horizontal' ? 'flex' : undefined,
    justifyContent: direction === 'horizontal' ? 'space-around' : undefined
  };
  const flipMoveStyles = deepMerge(defaultStyles, cssStyles);
  const renderedChildren = items.map((item, i) => renderItem(item, i));
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!animate ?
    /*#__PURE__*/
    // @bend-expect trellis-tokenized (the spread operator in styled component is cssStyles)
    _jsx(StyledOl, Object.assign({}, domProps, {
      "aria-label": description || undefined,
      role: listRole,
      $isHorizontal: direction === 'horizontal',
      $cssStyles: cssStyles,
      children: renderedChildren
    })) :
    /*#__PURE__*/
    // @bend-expect trellis-tokenized (FlipMove is a third party component)
    _jsx(FlipMove, Object.assign({}, domProps, {
      "data-test-id": "flip-move",
      appearAnimation: appearAnimation !== null && appearAnimation !== void 0 ? appearAnimation : undefined,
      delay: delay !== null && delay !== void 0 ? delay : undefined,
      disableAllAnimations: disableAllAnimations !== null && disableAllAnimations !== void 0 ? disableAllAnimations : undefined,
      duration: duration !== null && duration !== void 0 ? duration : undefined,
      easing: easing !== null && easing !== void 0 ? easing : undefined,
      enterAnimation: enterAnimation !== null && enterAnimation !== void 0 ? enterAnimation : undefined,
      getPosition: getPosition !== null && getPosition !== void 0 ? getPosition : undefined,
      leaveAnimation: leaveAnimation !== null && leaveAnimation !== void 0 ? leaveAnimation : undefined,
      maintainContainerHeight: maintainContainerHeight !== null && maintainContainerHeight !== void 0 ? maintainContainerHeight : undefined,
      onFinish: onFinish !== null && onFinish !== void 0 ? onFinish : undefined,
      onFinishAll: onFinishAll !== null && onFinishAll !== void 0 ? onFinishAll : undefined,
      onStart: onStart !== null && onStart !== void 0 ? onStart : undefined,
      onStartAll: onStartAll !== null && onStartAll !== void 0 ? onStartAll : undefined,
      staggerDurationBy: staggerDurationBy !== null && staggerDurationBy !== void 0 ? staggerDurationBy : undefined,
      staggerDelayBy: staggerDelayBy !== null && staggerDelayBy !== void 0 ? staggerDelayBy : undefined
      // @ts-ignore role is a valid HTML attribute passed through to the underlying element
      ,
      role: listRole,
      typeName: "ol",
      style: flipMoveStyles // default styles & styles passed in from token props
      ,
      children: renderedChildren
    })), /*#__PURE__*/_jsx(ScreenReaderOnly, {
      id: instructionsId,
      children: I18n.text('UISortableList.directions')
    })]
  });
};
export default UISortableList;